// La foto de lo que llegó mal, desde el teléfono del almacén.
//
// =====================================================================
// POR QUÉ UN <input type="file" capture> Y NO LA CÁMARA EN VIVO
// =====================================================================
// `getUserMedia` —la cámara dentro de la página— **exige contexto
// seguro**: HTTPS o `localhost`. La consola de la planta se sirve en
// `http://192.168.50.240`, así que ahí `getUserMedia` no existe: el
// navegador ni siquiera pregunta, devuelve `undefined` y la pantalla se
// queda con un botón que no hace nada. Y fallaría **justo en el almacén**,
// que es el único sitio donde esta pantalla se usa.
//
// `<input type="file" accept="image/*" capture="environment">` abre la
// cámara NATIVA del teléfono, funciona sobre http en la red local y
// funciona también detrás del túnel. Es menos vistoso y es el que llega
// al andén.
//
// =====================================================================
// LA FOTO SE ACHICA ANTES DE MANDARLA, Y SE DICE
// =====================================================================
// Una foto de teléfono de hoy pesa entre 3 y 8 MB. Tres de ellas no
// caben en el tope de 10 MB del adjunto, tardan una eternidad por el
// wifi del andén y, si no hay señal, engordan la cola de recepciones
// dentro del teléfono hasta que el navegador se niega a guardar más.
//
// Se reducen a 1600 px de lado mayor. **La huella se calcula sobre lo
// que se guarda**, así que lo guardado es lo comprobable — y el servidor
// vuelve a escribir toda imagen como WebP de todas formas (`adjuntos.py`),
// de modo que el original nunca era lo que quedaba en disco.
//
// Si el navegador no puede achicarla, se manda tal cual y el tope habla.
export const LADO_MAXIMO = 1600;
export const CALIDAD = 0.85;

// El mismo tope que `adjuntos.MAX_BYTES` en el servidor. Se comprueba
// aquí para decirlo ANTES de subir 10 MB por un wifi de almacén y que la
// respuesta sea que no cabían.
export const MAX_BYTES = 10 * 1024 * 1024;

// Los tres motivos, y son los del servidor (`acuse.MOTIVOS_EVIDENCIA`) y
// los del `CHECK` de la tabla. Con su nombre en llano al lado: en la
// pantalla no se lee «DAÑO» en mayúsculas de base de datos.
export const MOTIVOS = [
  { clave: 'FALTANTE', nombre: 'Faltó material' },
  { clave: 'DAÑO', nombre: 'Llegó dañado' },
  { clave: 'RECHAZO', nombre: 'Lo rechacé' },
];

export const nombreDeMotivo = (clave) => (
  MOTIVOS.find((m) => m.clave === clave)?.nombre || clave);

/**
 * ¿Esta recepción no se cierra sin foto?
 *
 * ES LA MISMA REGLA QUE `acuse.exige_evidencia` EN EL SERVIDOR, y está
 * escrita dos veces a propósito: aquí para poder decirlo mientras se
 * captura, y allá para negarse. Lo que no puede pasar es que digan cosas
 * distintas — la pantalla enseñaría un botón que promete guardar y el
 * servidor contestaría que no.
 *
 * El faltante NO se declara: sale de las cantidades. El daño y el
 * rechazo sí, porque una caja mojada con la pieza completa dentro no se
 * deduce de ningún número.
 */
export function exigeEvidencia({ hayFaltante, huboDano, huboRechazo }) {
  return Boolean(hayFaltante || huboDano || huboRechazo);
}

/**
 * ¿Se le puede decir ya que va a hacer falta una foto?
 *
 * NO ES LA MISMA PREGUNTA QUE `exigeEvidencia`, y confundirlas rompe el
 * conteo a ciegas. Lo que se exige al guardar es una cosa; lo que se
 * puede ENSEÑAR mientras alguien cuenta es otra:
 *
 *   · El daño y el rechazo los **declara** quien recibe. Pedirle la foto
 *     en cuanto los marca no le enseña nada que no acabara de escribir.
 *   · El faltante sale de comparar contra lo esperado. Enseñarlo
 *     mientras se cuenta a ciegas sería enseñar lo esperado — y bastaría
 *     con subir el número hasta que el aviso rojo desapareciera, que es
 *     exactamente el sesgo que el conteo a ciegas viene a quitar.
 *
 * Por eso el faltante se calla hasta `revelado`: el primer intento de
 * guardar, cuando las cantidades ya están tecleadas y la reconciliación
 * se iba a decir de todas formas.
 */
export function avisarDeLaFoto({ faltantes, huboDano, huboRechazo,
                                 aCiegas, revelado }) {
  if (huboDano || huboRechazo) return true;
  return faltantes.length > 0 && (!aCiegas || revelado);
}

/** Los renglones que traen menos de lo pedido. Uno por SKU, con su hueco. */
export function faltantesDe(renglones) {
  return renglones
    .filter((r) => r.cantidad_pedida != null
                   && Number(r.cantidad) < Number(r.cantidad_pedida))
    .map((r) => ({
      sku: r.sku,
      falta: Number(r.cantidad_pedida) - Number(r.cantidad),
    }));
}

// =====================================================================
// DE UN ARCHIVO DEL TELÉFONO A LO QUE VIAJA EN LA PETICIÓN
// =====================================================================

/** base64 de unos bytes, por trozos.
 *
 * POR TROZOS Y NO DE UNA: `String.fromCharCode(...arreglo)` con un
 * arreglo de tres millones de bytes se pasa del tope de argumentos del
 * motor y revienta con «Maximum call stack size exceeded», que es un
 * mensaje que no tiene nada que ver con una foto.
 */
export function aBase64(bytes) {
  const trozo = 0x8000;
  let s = '';
  for (let i = 0; i < bytes.length; i += trozo) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + trozo));
  }
  return btoa(s);
}

/** Achica la imagen si el navegador puede. Devuelve un Blob, o null. */
async function achicar(archivo) {
  // Todo esto existe en el navegador del almacén y en ninguno de los
  // entornos de prueba. Si falta cualquier pieza, se manda el original:
  // una foto grande que llega es mejor que una foto que no se toma.
  if (typeof createImageBitmap !== 'function'
      || typeof document === 'undefined'
      || typeof document.createElement !== 'function') return null;
  try {
    const mapa = await createImageBitmap(archivo);
    const lado = Math.max(mapa.width, mapa.height);
    if (lado <= LADO_MAXIMO) { mapa.close?.(); return null; }
    const escala = LADO_MAXIMO / lado;
    const lienzo = document.createElement('canvas');
    lienzo.width = Math.round(mapa.width * escala);
    lienzo.height = Math.round(mapa.height * escala);
    const ctx = lienzo.getContext('2d');
    if (!ctx) { mapa.close?.(); return null; }
    ctx.drawImage(mapa, 0, 0, lienzo.width, lienzo.height);
    mapa.close?.();
    return await new Promise((res) => {
      lienzo.toBlob((b) => res(b), 'image/jpeg', CALIDAD);
    });
  } catch {
    // Un formato que el navegador no sabe decodificar (HEIC en algunos
    // Android) llega aquí. Se manda el original y lo decide el servidor,
    // que sabe leer más formatos que el canvas.
    return null;
  }
}

/**
 * Una foto lista para viajar: `{motivo, contenido, nombre, nota}`.
 *
 * `contenido` va en base64 dentro del JSON de la recepción, y no como
 * subida aparte, porque **la recepción entera es una transacción**: la
 * foto, el acuse y las entradas al almacén se confirman o se revierten
 * juntas (ver `EvidenciaRecepcion` en el servidor). Una subida que se
 * enlaza después es una subida que a veces queda huérfana.
 */
export async function fotoDesdeArchivo(archivo, motivo) {
  const reducida = await achicar(archivo);
  const fuente = reducida || archivo;
  const bytes = new Uint8Array(await fuente.arrayBuffer());
  if (bytes.length > MAX_BYTES) {
    const mb = (bytes.length / 1024 / 1024).toFixed(1);
    throw new Error(
      `Esa foto pesa ${mb} MB y el tope es 10 MB. Tómala otra vez desde `
      + 'la cámara del teléfono, que la manda más ligera.');
  }
  return {
    motivo,
    contenido: aBase64(bytes),
    nombre: archivo.name || 'foto.jpg',
    nota: null,
    // Solo para la pantalla: cuánto pesa y de qué tipo es, para poder
    // enseñar la miniatura. No viaja al servidor —`carga()` de la cola
    // manda el objeto tal cual, así que lo que sobra aquí sobra allá—,
    // por eso `paraEnviar` lo quita.
    _bytes: bytes.length,
    _tipo: fuente.type || archivo.type || 'image/jpeg',
  };
}

/** Lo que se manda al servidor, sin lo que solo servía para pintar. */
export const paraEnviar = (fotos) => fotos.map(
  ({ motivo, contenido, nombre, nota }) => ({ motivo, contenido, nombre, nota }));
